import { ApplicationRef, inject, Injectable, NgZone } from '@angular/core';
import { ObservableFunctions } from '@campus/utils';
import { defer, Observable, of } from 'rxjs';
import { filter, map, RepeatConfig, switchMap, take, tap } from 'rxjs/operators';
const { repeatUntil } = ObservableFunctions;

@Injectable({
  providedIn: 'root',
})
export class ScrollToFragmentService {
  private appRef = inject(ApplicationRef);
  private ngZone = inject(NgZone);

  /**
   * Scrolls to a fragment after waiting for the application to stabilize
   * and the target element to exist in the DOM
   */
  public scrollToFragment(
    fragment: string | null,
    options?: {
      behavior?: ScrollBehavior;
      block?: ScrollLogicalPosition;
      inline?: ScrollLogicalPosition;
      maxRetries?: number;
      retryInterval?: number;
    }
  ): Observable<boolean> {
    if (!fragment) {
      return of(false);
    }

    const config = {
      behavior: 'smooth' as ScrollBehavior,
      block: 'start' as ScrollLogicalPosition,
      inline: 'nearest' as ScrollLogicalPosition,
      maxRetries: 50,
      retryInterval: 100,
      ...options,
    };

    return this.waitForStabilityAndElement(fragment, config.maxRetries, config.retryInterval).pipe(
      tap((element) => {
        if (element) {
          this.ngZone.runOutsideAngular(() => {
            element.scrollIntoView({
              behavior: config.behavior,
              block: config.block,
              inline: config.inline,
            });
          });
        }
      }),
      map((element) => !!element),
      take(1)
    );
  }

  /**
   * Scrolls to a fragment within a shadow DOM container
   * Useful for dcr-app-shell components
   */
  public scrollToFragmentInShadowDOM(
    fragment: string | null,
    containerSelector: string = 'dcr-app-shell',
    options?: {
      behavior?: ScrollBehavior;
      block?: ScrollLogicalPosition;
      inline?: ScrollLogicalPosition;
      maxRetries?: number;
      retryInterval?: number;
    }
  ): Observable<boolean> {
    if (!fragment) {
      return of(false);
    }

    const config = {
      behavior: 'smooth' as ScrollBehavior,
      block: 'start' as ScrollLogicalPosition,
      inline: 'nearest' as ScrollLogicalPosition,
      maxRetries: 50,
      retryInterval: 100,
      ...options,
    };

    return this.waitForStabilityAndShadowElement(
      fragment,
      containerSelector,
      config.maxRetries,
      config.retryInterval
    ).pipe(
      tap((element) => {
        if (element) {
          this.ngZone.runOutsideAngular(() => {
            element.scrollIntoView({
              behavior: config.behavior,
              block: config.block,
              inline: config.inline,
            });
          });
        }
      }),
      map((element) => !!element),
      take(1)
    );
  }

  private waitForStabilityAndElement(
    fragment: string,
    maxRetries: number,
    retryInterval: number
  ): Observable<HTMLElement | null> {
    return this.waitForElement(fragment, maxRetries, retryInterval).pipe(
      switchMap((element) => {
        if (!element) {
          return of(null);
        }
        return this.appRef.isStable.pipe(
          filter((isStable) => isStable),
          map(() => element),
          take(1)
        );
      })
    );
  }

  private waitForStabilityAndShadowElement(
    fragment: string,
    containerSelector: string,
    maxRetries: number,
    retryInterval: number
  ): Observable<HTMLElement | null> {
    return this.waitForShadowElement(fragment, containerSelector, maxRetries, retryInterval).pipe(
      switchMap((element) => {
        if (!element) {
          return of(null);
        }
        return this.appRef.isStable.pipe(
          filter((isStable) => isStable),
          map(() => element),
          take(1)
        );
      })
    );
  }

  private waitForElement(elementId: string, maxRetries: number, retryInterval: number): Observable<HTMLElement | null> {
    return this._waitForElement(() => document.getElementById(elementId), { count: maxRetries, delay: retryInterval });
  }

  private waitForShadowElement(
    elementId: string,
    containerSelector: string,
    maxRetries: number,
    retryInterval: number
  ): Observable<HTMLElement | null> {
    return this._waitForElement(
      () => {
        const container = document.querySelector(containerSelector);
        if (container?.shadowRoot) {
          return container.shadowRoot.getElementById(elementId);
        }
        // Fallback to regular DOM
        return document.getElementById(elementId);
      },
      { count: maxRetries, delay: retryInterval }
    );
  }

  private _waitForElement(selector: () => HTMLElement | null, config: RepeatConfig): Observable<HTMLElement | null> {
    // use defer to ensure the DOM is re-queried for each retry
    return defer(() => of(selector())).pipe(repeatUntil((el) => !!el, config));
  }
}
